import { request, RequestDocument } from 'graphql-request';
import GraphqlResponseError from '@/errors/GraphqlResponseError';
import { AuthService } from '@/services/auth.service';
import HasuraAuthenticationError from '@/errors/HasuraAuthenticationError';
import { Dict } from '@/types/utility';
import { envVariables } from '@/utils/envHelpers';
import JwtExpiredError from '@/errors/JwtExpiredError';
import parseJson, { JSONError } from 'parse-json';
import LocationHelper from '@/utils/LocationHelper';

// Constant timeout of 2 minutes (120000 milliseconds)
const GRAPHQL_REQUEST_TIMEOUT = 120000;

const makeGraphqlRequestBase = (url: string, requestHeaders: HeadersInit) => {
  return async (query: RequestDocument, variables: Dict<unknown>): Promise<unknown> => {
    // Create AbortController for timeout
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => {
      abortController.abort();
    }, GRAPHQL_REQUEST_TIMEOUT);

    try {
      const result = await request({
        url,
        document: query,
        variables,
        requestHeaders,
        signal: abortController.signal as any,
      });
      return result;
    } catch (e: any) {
      // Handle timeout/abort errors
      if (e.name === 'AbortError') {
        throw new Error(
          `GraphQL request timed out after ${GRAPHQL_REQUEST_TIMEOUT / 1000} seconds`,
        );
      }

      if (e.response?.errors?.length) {
        if (e.response.errors[0].extensions?.code === 'invalid-jwt') {
          throw new JwtExpiredError();
        }

        try {
          const payload: any = parseJson(e.response.errors[0].message);

          throw new GraphqlResponseError(
            payload.message instanceof Array ? payload.message[0] : payload.message,
            payload,
          );
        } catch (parseError) {
          if (parseError instanceof JSONError) {
            throw new GraphqlResponseError(e.response.errors[0].message, {
              message: e.response.errors[0].message,
              fields: null,
            });
          }
          throw parseError;
        }
      }

      throw e;
    } finally {
      // Clear the timeout to prevent memory leaks
      clearTimeout(timeoutId);
    }
  };
};

export const makeGraphqlRequestCatalog = (
  query: RequestDocument,
  variables: Dict<unknown>,
): Promise<unknown> => {
  if (envVariables.URL_GRAPHQL_CATALOG) {
    const catalogToken = AuthService.userData().hasura_token;

    if (!catalogToken) {
      throw new HasuraAuthenticationError('Hasura token is not provided');
    }

    return makeGraphqlRequestBase(envVariables.URL_GRAPHQL_CATALOG, {
      'x-hasura-admin-secret': catalogToken,
    })(query, variables);
  }
  throw new Error('Url must be provided');
};

export const makeGraphqlRequestCloud = async (
  query: RequestDocument,
  variables: Dict<unknown>,
  attempt = 1,
  shareToken?: string,
  sharedObjectId?: number,
): Promise<unknown> => {
  const token = shareToken
    ? await AuthService.getHasuraAnonymousToken(shareToken, String(sharedObjectId))
    : await AuthService.tokenRefreshedIfOld();

  try {
    const graphqlUrl = `${LocationHelper.getBaseUrl() ?? ''}/hasura/v1/graphql`;
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    const result = await makeGraphqlRequestBase(graphqlUrl, headers)(query, variables);
    return result;
  } catch (e: any) {
    if (e instanceof JwtExpiredError && attempt < 2) {
      return makeGraphqlRequestCloud(query, variables, attempt + 1, shareToken, sharedObjectId);
    }
    throw e;
  }
};
